import React, { useMemo, useRef } from "react"
import { Button, Drawer, Select, Tag } from "antd"
import type { SelectProps } from "antd"
import { numericVariables } from "features/Dashboard/runtime/prepScript"
import { shownNames } from "../../model/shownLabels"
import { useTabSpecStore } from "../../runtime/tabSpecStore"
import ConditionGroups, { DraftGroup } from "../Inspector/ConditionGroups.component"

/** The select's value for no weight (a select cannot hold null). */
const NO_WEIGHT = "__none__"
/** What "No weight" does to a weight the preparation applies. */
const NO_WEIGHT_HINT =
  "No weight: a weight set by a pipeline or script is switched off for the tables. Choose it here to use it."
/** What the book filter does. */
const BOOK_FILTER_HINT = "Every table in this tab spec counts only these respondents."

export interface DataDrawerProps {
  open: boolean
  onClose: () => void
  /** The questionnaire's versions; the choice is shown only when there are two or more. */
  variants: string[]
  /** The names of the project's saved pipelines and scripts; undefined while they load or when they failed to. */
  sources: { pipelines: string[]; scripts: string[] } | undefined
  onRestart: () => void
  getContainer?: () => HTMLElement
}

/**
 * What the tables are run on: the questionnaire version, the saved
 * pipelines and scripts that prepare the data, the weight, and the book
 * filter, which every table counts within. A change of the preparation or
 * the weight prepares the data again. A script is marked "script"; a name the
 * spec lists that the project no longer has turns red and is marked "not
 * found", as the dashboards' Data panel. The book filter is offered once the
 * questions are read.
 */
const DataDrawer: React.FC<DataDrawerProps> = ({
  open,
  onClose,
  variants,
  sources,
  onRestart,
  getContainer,
}) => {
  const data = useTabSpecStore((s) => s.document.data)
  const variant = useTabSpecStore((s) => s.document.variant)
  const codebook = useTabSpecStore((s) => s.codebook)
  const labels = useTabSpecStore((s) => s.document.labels)
  const updateData = useTabSpecStore((s) => s.updateData)
  const updateVariant = useTabSpecStore((s) => s.updateVariant)
  const catalogue = useTabSpecStore((s) => s.catalogue)
  const bookFilter = useTabSpecStore((s) => s.document.filter)
  const setBookFilter = useTabSpecStore((s) => s.setBookFilter)
  // The book filter's groups as shown, an unfinished one included, while the drawer is closed.
  const bookDraft = useRef<DraftGroup[] | null>(null)
  const numeric = useMemo(() => numericVariables(codebook), [codebook])
  const names = useMemo(() => shownNames(labels), [labels])

  const missing = (name: string): boolean =>
    sources !== undefined &&
    !sources.pipelines.includes(name) &&
    !sources.scripts.includes(name)
  const isScript = (name: string): boolean =>
    sources !== undefined && sources.scripts.includes(name)
  const tagRender: SelectProps["tagRender"] = ({ value, label, closable, onClose: remove }) => {
    const name = String(value)
    const gone = missing(name)
    return (
      <Tag
        closable={closable}
        onClose={remove}
        onMouseDown={(e) => e.preventDefault()}
        className={gone ? "ts-pipeline-tag ts-pipeline-tag--missing" : "ts-pipeline-tag"}
      >
        {label ?? name}
        {isScript(name) && <span className="ts-chip ts-pipeline-tag__kind">script</span>}
        {gone && (
          <span className="ts-chip ts-pipeline-tag__missing" data-tone="bad">
            not found
          </span>
        )}
      </Tag>
    )
  }

  // Pipelines, then scripts, each in the order the project lists them; a
  // group with nothing in it is left out.
  const prepOptions = sources
    ? [
        { label: "Pipelines", names: sources.pipelines },
        { label: "Scripts", names: sources.scripts },
      ]
        .filter((g) => g.names.length > 0)
        .map((g) => ({ label: g.label, options: g.names.map((n) => ({ value: n, label: n })) }))
    : []

  const weight = data.weight
  // Each weight as the tables show its name, its label as the tooltip; a
  // search finds both. The value is the label: the script names it.
  const weightOption = (v: string): { value: string; label: string; title: string; search: string } => {
    const shown = names.variable(v)
    return { value: v, label: shown, title: v, search: shown === v ? v : `${shown}\n${v}` }
  }
  const weightOptions = [
    { value: NO_WEIGHT, label: "No weight", title: "No weight", search: "No weight" },
    ...(weight !== null && !numeric.includes(weight) ? [weightOption(weight)] : []),
    ...numeric.map(weightOption),
  ]

  return (
    <Drawer
      open={open}
      onClose={onClose}
      getContainer={getContainer}
      rootClassName="ts-drawer"
      placement="right"
      width={440}
      title="Data"
    >
      <div className="ts-drawer__body">
        {variants.length >= 2 && (
          <section className="ts-field" aria-label="Questionnaire version">
            <h3 className="ts-field__label">Questionnaire version</h3>
            <Select
              size="small"
              aria-label="Questionnaire version"
              className="ts-field__select"
              value={variant ?? variants[0]}
              options={variants.map((v) => ({ value: v, label: v }))}
              onChange={(v: string) => updateVariant(v)}
            />
          </section>
        )}
        <section className="ts-field" aria-label="Data preparation">
          <h3 className="ts-field__label">Data preparation</h3>
          <Select
            mode="multiple"
            size="small"
            aria-label="Data preparation"
            className="ts-field__select"
            placeholder="No preparation"
            value={data.pipelines}
            options={prepOptions}
            tagRender={tagRender}
            onChange={(chosen: string[]) => updateData({ pipelines: chosen })}
          />
          <p className="ts-hint">
            Saved pipelines and scripts from the console, run in this order before any
            table. The weight chosen below is applied after them.
          </p>
        </section>
        <section className="ts-field" aria-label="Weight">
          <h3 className="ts-field__label">Weight</h3>
          <Select
            showSearch
            size="small"
            aria-label="Weight"
            className="ts-field__select"
            value={weight ?? NO_WEIGHT}
            options={weightOptions}
            optionFilterProp="search"
            notFoundContent="No numeric variable matches"
            onChange={(v: string) => updateData({ weight: v === NO_WEIGHT ? null : v })}
          />
          {/* The weight line always follows the preparation, bare under no weight. */}
          {weight === null && <p className="ts-hint">{NO_WEIGHT_HINT}</p>}
        </section>
        <section className="ts-field" aria-label="Book filter">
          <h3 className="ts-field__label">Book filter</h3>
          {catalogue ? (
            <ConditionGroups
              value={bookFilter ?? []}
              onChange={setBookFilter}
              catalogue={catalogue}
              names={names}
              draft={bookDraft}
            />
          ) : (
            <p className="ts-hint">The questions are still being read.</p>
          )}
          <p className="ts-hint">{BOOK_FILTER_HINT}</p>
        </section>
        <section className="ts-field" aria-label="Data session">
          <h3 className="ts-field__label">Data session</h3>
          <Button size="small" onClick={onRestart}>
            Restart session
          </Button>
          <p className="ts-hint">
            Opens the data again and prepares it from the start.
          </p>
        </section>
      </div>
    </Drawer>
  )
}

export default DataDrawer
